/**
 * Converts a number to words and formats currency with correct spelling based on language
 * @param {number} amount - The amount to convert
 * @param {Object} currency - Currency object with id, fullText, and locale
 * @param {string} language - Language code (e.g., 'ru', 'en')
 * @returns {string} - Formatted amount in words with proper currency spelling
 */

// Russian number to words conversion
const numberToWordsRu = (num) => {
  const ones = ['', 'один', 'два', 'три', 'четыре', 'пять', 'шесть', 'семь', 'восемь', 'девять']
  const teens = ['десять', 'одиннадцать', 'двенадцать', 'тринадцать', 'четырнадцать', 'пятнадцать', 'шестнадцать', 'семнадцать', 'восемнадцать', 'девятнадцать']
  const tens = ['', '', 'двадцать', 'тридцать', 'сорок', 'пятьдесят', 'шестьдесят', 'семьдесят', 'восемьдесят', 'девяносто']
  const hundreds = ['', 'сто', 'двести', 'триста', 'четыреста', 'пятьсот', 'шестьсот', 'семьсот', 'восемьсот', 'девятьсот']
  
  if (num === 0) return 'ноль'
  
  let result = ''
  
  // Thousands
  if (num >= 1000) {
    const thousandsNum = Math.floor(num / 1000)
    if (thousandsNum === 1) result += 'одна тысяча '
    else if (thousandsNum >= 2 && thousandsNum <= 4) result += ones[thousandsNum] + ' тысячи '
    else if (thousandsNum < 10) result += ones[thousandsNum] + ' тысяч '
    else {
      // For numbers >= 10000, recursively convert thousands
      result += numberToWordsRu(thousandsNum) + ' тысяч '
    }
    num %= 1000
  }
  
  // Hundreds
  if (num >= 100) {
    result += hundreds[Math.floor(num / 100)] + ' '
    num %= 100
  }
  
  // Tens and ones
  if (num >= 20) {
    result += tens[Math.floor(num / 10)] + ' '
    num %= 10
  } else if (num >= 10) {
    result += teens[num - 10] + ' '
    num = 0
  }
  
  if (num > 0) {
    result += ones[num] + ' '
  }
  
  return result.trim()
}

// English number to words conversion
const numberToWordsEn = (num) => {
  const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine']
  const teens = ['ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen']
  const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety']
  const hundreds = ['', 'one hundred', 'two hundred', 'three hundred', 'four hundred', 'five hundred', 'six hundred', 'seven hundred', 'eight hundred', 'nine hundred']
  
  if (num === 0) return 'zero'
  
  let result = ''
  
  // Thousands
  if (num >= 1000) {
    const thousandsNum = Math.floor(num / 1000)
    if (thousandsNum < 10) {
      result += ones[thousandsNum] + ' thousand '
    } else {
      // For numbers >= 10000, recursively convert thousands
      result += numberToWordsEn(thousandsNum) + ' thousand '
    }
    num %= 1000
  }
  
  // Hundreds
  if (num >= 100) {
    result += hundreds[Math.floor(num / 100)] + ' '
    num %= 100
  }
  
  // Tens and ones
  if (num >= 20) {
    result += tens[Math.floor(num / 10)] + ' '
    num %= 10
  } else if (num >= 10) {
    result += teens[num - 10] + ' '
    num = 0
  }
  
  if (num > 0) {
    result += ones[num] + ' '
  }
  
  return result.trim()
}

// Russian currency declensions
const getRussianCurrencyForm = (amount, currencyId, originalFullText) => {
  const lastDigit = amount % 10
  const lastTwoDigits = amount % 100
  
  // Special cases for 11-19
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
    switch (currencyId) {
      case 'kzt':
        return 'тенге'
      case 'rub':
        return 'рублей'
      case 'kgs':
        return 'сомов'
      case 'uah':
        return 'гривен'
      case 'amd':
        return 'драмов'
      default:
        return originalFullText || 'единиц'
    }
  }
  
  // Cases for 1, 2-4, 5-9
  if (lastDigit === 1) {
    switch (currencyId) {
      case 'kzt':
        return 'тенге'
      case 'rub':
        return 'рубль'
      case 'kgs':
        return 'сом'
      case 'uah':
        return 'гривня'
      case 'amd':
        return 'драм'
      default:
        return originalFullText || 'единица'
    }
  } else if (lastDigit >= 2 && lastDigit <= 4) {
    switch (currencyId) {
      case 'kzt':
        return 'тенге'
      case 'rub':
        return 'рубля'
      case 'kgs':
        return 'сома'
      case 'uah':
        return 'гривни'
      case 'amd':
        return 'драма'
      default:
        return originalFullText || 'единицы'
    }
  } else {
    switch (currencyId) {
      case 'kzt':
        return 'тенге'
      case 'rub':
        return 'рублей'
      case 'kgs':
        return 'сомов'
      case 'uah':
        return 'гривен'
      case 'amd':
        return 'драмов'
      default:
        return originalFullText || 'единиц'
    }
  }
}

// English currency forms
const getEnglishCurrencyForm = (amount, currencyId, originalFullText) => {
  const lastDigit = amount % 10
  const lastTwoDigits = amount % 100
  
  // Special cases for 11-19
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
    switch (currencyId) {
      case 'usd':
      case 'aud':
      case 'cad':
      case 'nzd':
      case 'sgd':
      case 'hkd':
        return 'dollars'
      case 'eur':
        return 'euros'
      case 'gbp':
        return 'pounds'
      case 'jpy':
        return 'yen'
      case 'chf':
        return 'francs'
      case 'sek':
      case 'nok':
      case 'dkk':
        return 'kronor'
      default:
        return originalFullText + 's'
    }
  }
  
  // Cases for 1, 2-9
  if (lastDigit === 1) {
    switch (currencyId) {
      case 'usd':
      case 'aud':
      case 'cad':
      case 'nzd':
      case 'sgd':
      case 'hkd':
        return 'dollar'
      case 'eur':
        return 'euro'
      case 'gbp':
        return 'pound'
      case 'jpy':
        return 'yen'
      case 'chf':
        return 'franc'
      case 'sek':
      case 'nok':
      case 'dkk':
        return 'krona'
      default:
        return originalFullText
    }
  } else {
    switch (currencyId) {
      case 'usd':
      case 'aud':
      case 'cad':
      case 'nzd':
      case 'sgd':
      case 'hkd':
        return 'dollars'
      case 'eur':
        return 'euros'
      case 'gbp':
        return 'pounds'
      case 'jpy':
        return 'yen'
      case 'chf':
        return 'francs'
      case 'sek':
      case 'nok':
      case 'dkk':
        return 'kronor'
      default:
        return originalFullText + 's'
    }
  }
}

// Main function
export const currencyToWords = (amount, currency, language = 'ru') => {
  if (!amount || amount < 0) return ''
  
  const integerAmount = Math.floor(amount)
  
  // Convert number to words based on language
  let numberInWords
  if (language === 'ru' || language === 'ru-RU') {
    numberInWords = numberToWordsRu(integerAmount)
  } else {
    numberInWords = numberToWordsEn(integerAmount)
  }
  
  // Get currency form based on language and amount
  let currencyForm
  if (language === 'ru' || language === 'ru-RU') {
    currencyForm = getRussianCurrencyForm(integerAmount, currency.id, currency.fullText)
  } else {
    currencyForm = getEnglishCurrencyForm(integerAmount, currency.id, currency.fullText)
  }
  
  return `${numberInWords} ${currencyForm}`
}

// Convenience function that uses the user's locale from app state
export const currencyToWordsAuto = (amount, currency, userLocale = 'ru') => {
  return currencyToWords(amount, currency, userLocale)
}
